import { useState } from 'react';
import { useTheme, themeColors } from '../ThemeContext';
import { notes, Note } from '../../../mocks/boothData';
import { useTranslation } from 'react-i18next';

const STATUS_CONFIG = {
  Visited: { color: 'bg-emerald-500', textColor: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: 'ri-checkbox-circle-fill', lightColor: 'text-emerald-600', lightBg: 'bg-emerald-50' },
  'Follow-up': { color: 'bg-[#00d4ff]', textColor: 'text-[#00d4ff]', bg: 'bg-[#00d4ff]/10', icon: 'ri-refresh-line', lightColor: 'text-blue-600', lightBg: 'bg-blue-50' },
  Interested: { color: 'bg-amber-400', textColor: 'text-amber-400', bg: 'bg-amber-400/10', icon: 'ri-star-fill', lightColor: 'text-amber-600', lightBg: 'bg-amber-50' },
  'Not Relevant': { color: 'bg-gray-500', textColor: 'text-gray-400', bg: 'bg-gray-500/10', icon: 'ri-close-circle-fill', lightColor: 'text-gray-500', lightBg: 'bg-gray-100' },
};

const STATUSES: Note['visitStatus'][] = ['Visited', 'Follow-up', 'Interested', 'Not Relevant'];

const NotesScreen = () => {
  const { theme } = useTheme();
  const c = themeColors[theme];
  const isDark = theme === 'dark';
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>('n001');
  const [filterStatus, setFilterStatus] = useState<Note['visitStatus'] | 'All'>('All');
  const [noteList, setNoteList] = useState(notes);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const getStatusLabel = (s: Note['visitStatus'] | 'All') => {
    if (s === 'All') return t('notes.filter.all');
    if (s === 'Visited') return t('notes.status.visited');
    if (s === 'Follow-up') return t('notes.status.followup');
    if (s === 'Interested') return t('notes.status.interested');
    return t('notes.status.notRelevant');
  };

  const filtered = noteList.filter(n => filterStatus === 'All' || n.visitStatus === filterStatus);

  const updateStatus = (id: string, status: Note['visitStatus']) => {
    setNoteList(prev => prev.map(n => n.id === id ? { ...n, visitStatus: status } : n));
  };
  const saveNote = (id: string) => {
    setNoteList(prev => prev.map(n => n.id === id ? { ...n, note: editText } : n));
    setEditingNote(null);
  };
  const startEdit = (note: Note) => { setEditingNote(note.id); setEditText(note.note); setExpandedId(note.id); };

  const summaryChips = [
    { status: 'All' as const, count: noteList.length, icon: 'ri-list-check', color: isDark ? 'text-gray-300' : 'text-gray-600' },
    { status: 'Follow-up' as Note['visitStatus'], count: noteList.filter(n => n.visitStatus === 'Follow-up').length, icon: STATUS_CONFIG['Follow-up'].icon, color: isDark ? STATUS_CONFIG['Follow-up'].textColor : STATUS_CONFIG['Follow-up'].lightColor },
    { status: 'Interested' as Note['visitStatus'], count: noteList.filter(n => n.visitStatus === 'Interested').length, icon: STATUS_CONFIG['Interested'].icon, color: isDark ? STATUS_CONFIG['Interested'].textColor : STATUS_CONFIG['Interested'].lightColor },
    { status: 'Visited' as Note['visitStatus'], count: noteList.filter(n => n.visitStatus === 'Visited').length, icon: STATUS_CONFIG['Visited'].icon, color: isDark ? STATUS_CONFIG['Visited'].textColor : STATUS_CONFIG['Visited'].lightColor },
  ];

  return (
    <div className={`flex flex-col min-h-screen pb-24 ${c.bg}`}>
      <div className={`sticky top-0 z-20 ${c.navBg} backdrop-blur-xl border-b ${c.navBorder} px-5 pt-12 pb-4`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className={`text-xl font-black ${c.text}`}>{t('notes.title')}</h1>
            <p className={`text-xs ${c.textMuted}`}>{noteList.length} {t('notes.subtitle')}</p>
          </div>
          <div className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap ${isDark ? 'bg-[#00d4ff]/10 text-[#00d4ff]' : 'bg-blue-50 text-blue-600'}`}>
            <i className="ri-add-line"></i> {t('notes.add')}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 mb-3">
          {summaryChips.map(chip => (
            <button key={chip.status} onClick={() => setFilterStatus(chip.status)} className={`flex flex-col items-center gap-0.5 p-2 rounded-xl border transition-all cursor-pointer whitespace-nowrap ${filterStatus === chip.status ? isDark ? 'bg-[#1e2d47] border-[#00d4ff]/40' : 'bg-blue-50 border-blue-200' : `${c.bgCard} ${c.border}`}`}>
              <i className={`${chip.icon} text-sm ${chip.color}`}></i>
              <span className={`text-sm font-black ${c.text}`}>{chip.count}</span>
              <span className={`text-[9px] ${c.textMuted}`}>{getStatusLabel(chip.status)}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {(['All', ...STATUSES] as const).map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer border transition-all ${filterStatus === s ? isDark ? 'bg-[#00d4ff] text-[#08090F] border-[#00d4ff]' : 'bg-[#0070E0] text-white border-[#0070E0]' : `${c.bgCard} ${c.text} ${c.border}`}`}>
              {getStatusLabel(s)}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-4 flex flex-col gap-3">
        {filtered.map(note => {
          const statusCfg = STATUS_CONFIG[note.visitStatus];
          const isExpanded = expandedId === note.id;
          const isEditing = editingNote === note.id;
          return (
            <div key={note.id} className={`rounded-2xl border overflow-hidden transition-all duration-300 ${c.bgCard} ${c.border} ${note.priority === 'high' ? 'border-l-4 border-l-rose-500' : ''}`}>
              <button onClick={() => setExpandedId(isExpanded ? null : note.id)} className="w-full text-left p-4 cursor-pointer">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${isDark ? `${statusCfg.bg} ${statusCfg.textColor}` : `${statusCfg.lightBg} ${statusCfg.lightColor}`}`}>
                        <i className={`${statusCfg.icon} text-[10px]`}></i>{getStatusLabel(note.visitStatus)}
                      </span>
                      <span className={`text-[10px] ${c.textMuted}`}>{note.boothNumber} • {note.industry}</span>
                    </div>
                    <h3 className={`text-sm font-black ${c.text} truncate`}>{note.company}</h3>
                    {note.contactName && <p className={`text-[11px] ${c.textMuted} mt-0.5`}><i className="ri-user-line mr-1"></i>{note.contactName}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <i className={`ri-${isExpanded ? 'arrow-up' : 'arrow-down'}-s-line text-sm ${c.textMuted}`}></i>
                    <span className={`text-[10px] ${c.textMuted}`}>{note.timestamp.split(' ')[1]}</span>
                  </div>
                </div>
              </button>
              {isExpanded && (
                <div className={`px-4 pb-4 border-t ${c.border}`}>
                  <div className="mt-3 mb-3">
                    {isEditing ? (
                      <div>
                        <textarea value={editText} onChange={e => setEditText(e.target.value)} rows={4} className={`w-full text-xs ${c.text} ${isDark ? 'bg-[#0D1117]' : 'bg-gray-50'} border ${c.border} rounded-xl p-3 outline-none resize-none`} />
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => saveNote(note.id)} className={`flex-1 py-2 text-xs font-bold rounded-xl cursor-pointer whitespace-nowrap ${isDark ? 'bg-[#00d4ff] text-[#08090F]' : 'bg-[#0070E0] text-white'}`}>{t('notes.save')}</button>
                          <button onClick={() => setEditingNote(null)} className={`px-4 py-2 text-xs font-semibold rounded-xl cursor-pointer whitespace-nowrap border ${c.bgCard} ${c.border} ${c.textMuted}`}>{t('notes.cancel')}</button>
                        </div>
                      </div>
                    ) : (
                      <div className={`p-3 rounded-xl ${isDark ? 'bg-[#0D1117]' : 'bg-gray-50'} border ${c.border}`}>
                        <p className={`text-xs ${c.textSecondary} leading-relaxed`}>{note.note || 'No notes added yet.'}</p>
                      </div>
                    )}
                  </div>
                  {note.contactEmail && (
                    <div className={`flex items-center gap-2 mb-3 p-2.5 rounded-xl border ${c.border} ${isDark ? 'bg-[#0D1117]' : 'bg-gray-50'}`}>
                      <i className={`ri-mail-line text-sm ${isDark ? 'text-[#00d4ff]' : 'text-blue-600'}`}></i>
                      <span className={`text-xs ${c.textSecondary}`}>{note.contactEmail}</span>
                    </div>
                  )}
                  <div className="mb-3">
                    <p className={`text-[10px] font-bold uppercase tracking-widest ${c.textMuted} mb-2`}>{t('notes.updateStatus')}</p>
                    <div className="flex gap-2 flex-wrap">
                      {STATUSES.map(s => {
                        const cfg = STATUS_CONFIG[s];
                        const isActive = note.visitStatus === s;
                        return (
                          <button key={s} onClick={() => updateStatus(note.id, s)} className={`px-3 py-1.5 rounded-full text-[10px] font-bold transition-all cursor-pointer whitespace-nowrap border ${isActive ? isDark ? `${cfg.bg} ${cfg.textColor} border-transparent` : `${cfg.lightBg} ${cfg.lightColor} border-transparent` : `${c.bgCard} ${c.textMuted} ${c.border}`}`}>
                            <i className={`${cfg.icon} mr-1`}></i>{getStatusLabel(s)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(note)} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap border transition-all ${c.bgCard} ${c.border} ${c.textMuted}`}>
                      <i className="ri-edit-line"></i> {t('notes.editNote')}
                    </button>
                    <button className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${isDark ? 'bg-[#00d4ff]/10 text-[#00d4ff] border border-[#00d4ff]/20' : 'bg-blue-50 text-blue-600 border border-blue-100'}`}>
                      <i className="ri-share-forward-line"></i> {t('notes.share')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className={`text-center py-16 ${c.textMuted}`}>
            <i className="ri-sticky-note-2-line text-4xl mb-3 block"></i>
            <p className="text-sm font-bold">{t('notes.noNotes')}</p>
            <p className="text-xs mt-1">{t('notes.noNotesDesc')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesScreen;
